import {useCallback, useMemo, useRef, useState} from "react";
import {useMemoizedFn, useRequest} from "ahooks";
import {debounce, isEmpty} from 'lodash-es';
import {CheckOptionType} from "@/pages/datalab/components/FilterSelectorWithTag/type";
import {
  getNullTextOfFilter,
  sanitizeReportDimensionParams,
} from '@/pages/datalab/utils/utils';
import {
  accentIncludes,
  accentIndexOf,
  normalizeAccentsLower,
} from '@/pages/datalab/utils/accentMap';
import {batchedUpdates} from '@home/utils/utils';

interface ParamsType {
  game_code: string;
  table_id: string;
  dim_name: string;
  dim_has_query_table?: boolean;
  page_size: number;
  page_no: number;
  system?: string;
  keyword?: string;
}

interface PropsType {
  requestOptions: (params: ParamsType) => Promise<unknown>;
  params?: ParamsType;
  thenCallback?: (options: CheckOptionType[], lastParams: any[] ) => void;
  refreshDeps?: any[];
  manual?: boolean;
  errorCallback?: (error: any) => void;
}

interface IValueListItem {
  value: string | number | null;
  display?: string;
  display_en?: string;
  text?: string;
  text_en?: string;
}

// 命中比较已切换为"忽略大小写 + 忽略重音字符"的归一化比较
function matchScore(s: string, key: string): number {
  const isSpecialChar = (c: string): boolean => !/[a-zA-Z0-9]/.test(c);

  if (key.length === 0) {
    if (s.length === 0) {
      return -1;
    }

    let hasSpecialChar = false;
    for (const c of s) {
      if (isSpecialChar(c)) {
        hasSpecialChar = true;
        break;
      }
    }

    if (hasSpecialChar) {
      return 1000000 + s.length;
    }

    return 0;
  }

  if (normalizeAccentsLower(s) === normalizeAccentsLower(key)) {
    return -1;
  }

  const offset = accentIndexOf(s, key);
  return offset * 1000 + s.length;
}

function searchOptions(source: {text: string; text_en?: string; value: string|null;}[], target: string) {
  const searchResult = source.filter(opt =>
    accentIncludes(opt?.text, target) ||
    accentIncludes(opt?.text_en, target) ||
    accentIncludes(String(opt?.value ?? ''), target),
  );

  return searchResult?.map?.((opt) => ({
    ...opt,
    score: matchScore(opt.text, target)
  }))
    ?.sort?.((a, b) => a?.score - b?.score)
    ?.map?.((opt) => {
      delete opt?.score;
      return opt;
    });
}

export const useStringSelectorProps = (props: PropsType) => {

  const { requestOptions, params, thenCallback, errorCallback, ...requestConfig} = props;

  const initDataRef = useRef({
    options: [],
    total: 0,
    isClearSearchWord: true,
  });

  const [searchLoading, setSearchLoading] = useState(false);
  const [originalOptions, setOriginalOptions] = useState<CheckOptionType[]>([]);
  const [options, setOptions] = useState<CheckOptionType[]>([]);
  const requestDimensionOptions = (newParams?: ParamsType) =>
    requestOptions(sanitizeReportDimensionParams(newParams ?? params));

  const {loading=false, error, runAsync, run,} = useRequest(requestDimensionOptions, {
    ...requestConfig,
    onSuccess: (data: any, lastParams) => {
      batchedUpdates(() => {
        const valueList = data?.value_list?.map((item: IValueListItem) => {
          const value = getNullTextOfFilter(item.value);
          const display = item.display ?? item.text ?? value;
          const displayEn = item.display_en ?? item.text_en ?? display;
          return {
            value,
            text: display,
            text_en: displayEn,
            title: display,
          };
        }) || [];

        const result = thenCallback?.(valueList, (isEmpty(lastParams) ? [params] : lastParams));

        if (!lastParams[0]?.keyword) {
          initDataRef.current.options = result?.options || valueList;
          initDataRef.current.total = data?.total_size;
          initDataRef.current.allData = result?.allData || data.is_full;
        }
        initDataRef.current.isClearSearchWord = !lastParams?.[0]?.keyword;
        initDataRef.current.params = lastParams?.[0];
        setOptions(result?.options ?? valueList);
        setOriginalOptions(valueList);
        errorCallback?.({});
      });
    },
    onError: (error, params) => {
      errorCallback?.(error);
      console.error("筛选器选项请求错误: ", error);
      initDataRef.current.isClearSearchWord = !params?.[0]?.keyword;
      setOptions([]);
      setOriginalOptions([]);
    },
    onFinally: () => {
      setSearchLoading(false);
    }
  });

  const debouncedSearchOptions = useCallback(debounce((params) => {
    run(params);
  }, 600), [run]);

  const buildRequestParams = (keyword?: string): ParamsType => {
    const base = { ...(initDataRef.current.params ?? params) } as ParamsType;
    return sanitizeReportDimensionParams({
      ...base,
      keyword: keyword?.trim(),
    });
  };

  const onSearchKeywordChange = useMemoizedFn((keyword = '') => {

    initDataRef.current.searchWord = keyword;
    if (keyword?.trim()) {
      initDataRef.current.isClearSearchWord = false;
      if (initDataRef.current.allData) {
        setOptions(searchOptions(initDataRef.current.options, keyword.trim()));
      } else {
        setSearchLoading(true);
        debouncedSearchOptions(buildRequestParams(keyword));
      }

    } else {
      initDataRef.current.isClearSearchWord = true;
      if (initDataRef.current.allData && !isEmpty(initDataRef.current.options)) {
        setOptions(initDataRef.current.options);
      } else {
        setSearchLoading(true);
        debouncedSearchOptions(buildRequestParams());
      }
    }

  });

  return {
    loading: searchLoading || loading,
    error,
    options,
    originalOptions,
    getStringOptionsAsync: runAsync,
    getStringOptions: run,
    onSearchKeywordChange,
    isClearSearchWord: initDataRef.current.isClearSearchWord,
    lastParams: {...(initDataRef.current.params ?? params)},
    keyword: initDataRef.current.searchWord,
  };

};

export const useTransformNullValue = (props: { values: any; onCheckChange: any; }) => {
  const {values, onCheckChange} = props;
  const finallyValues = useMemo(() => values?.map?.((item: string | null) => getNullTextOfFilter(item)), [values]);

  const onChange = useCallback((values: string[], ...others: any) => {
    onCheckChange?.(values?.map?.((item) => {
      if (item === '[NULL]') return null;
      if (item === '[empty string]') return '';
      return item;
    }), ...others);
  }, [onCheckChange]);

  return {
    finallyValues,
    onChange
  };

};

// 所有业务的默认筛选范围都改成了all time
export const getDefaultStringOptionTimeFilter = () => ({
  lastCount: 'all_time',
  dateType: '',
  value: [],
  operation: 'recent',
  last_day: '-1',
  isCustomOption: false,
  is_full: true,
});
